import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowUpRight, CalendarClock, Ghost, PhoneOff, Moon, ClipboardCheck, Wrench, Users, ShieldCheck, Sparkles, Quote, PhoneCall } from 'lucide-react';
import { getCalApi } from '@calcom/embed-react';
import SEOHead from '../components/UI/SEOHead';
import { waLink, calLink, CAL_UI_CONFIG } from '../config';
import Reveal from '../components/UI/Reveal';
import Magnetic from '../components/UI/Magnetic';

const PAIN_POINTS = [
  {
    icon: Ghost,
    title: 'Pacientes fantasma',
    desc: 'Agendan pero no llegan ni confirman, dejando espacios vacíos en tu agenda.',
  },
  {
    icon: PhoneOff,
    title: 'Recepción saturada',
    desc: 'Tu equipo responde los mismos mensajes de WhatsApp una y otra vez, incluso fuera de horario.',
  },
  {
    icon: Moon,
    title: 'Cero atención 24/7',
    desc: 'Un paciente que escribe a las 10pm y no recibe respuesta, agenda en otra clínica.',
  },
];

const INCLUDES = [
  {
    icon: ClipboardCheck,
    title: 'Diagnóstico operativo gratuito',
    desc: 'Evaluamos los cuellos de botella reales de tu clínica en una llamada de 15-20 min.',
  },
  {
    icon: Wrench,
    title: 'Implementación 100% bonificada',
    desc: 'Diseñamos e integramos el sistema a tu flujo de trabajo, sin costo de desarrollo.',
  },
  {
    icon: Users,
    title: 'Acompañamiento directo',
    desc: 'Sesiones uno a uno con el equipo técnico para ajustar la herramienta a tus necesidades reales.',
  },
];

const PilotProgramPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const heroSentinelRef = useRef<HTMLDivElement>(null);
  const finalCtaRef = useRef<HTMLDivElement>(null);
  const [pastHero, setPastHero] = useState(false);
  const [reachedFinalCta, setReachedFinalCta] = useState(false);

  useEffect(() => {
    (async () => {
      const cal = await getCalApi();
      cal('ui', { ...CAL_UI_CONFIG, hideEventTypeDetails: true });
    })();
  }, []);

  // Barra de agendamiento fija en móvil: aparece una vez que el hero (con su
  // propio CTA) sale de vista y se oculta al llegar a la tarjeta de CTA
  // final, para no duplicar el mismo botón dos veces en pantalla a la vez.
  useEffect(() => {
    const heroObserver = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting), { threshold: 0 });
    const finalObserver = new IntersectionObserver(([entry]) => setReachedFinalCta(entry.isIntersecting), { threshold: 0 });
    if (heroSentinelRef.current) heroObserver.observe(heroSentinelRef.current);
    if (finalCtaRef.current) finalObserver.observe(finalCtaRef.current);
    return () => {
      heroObserver.disconnect();
      finalObserver.disconnect();
    };
  }, []);

  const showStickyBar = pastHero && !reachedFinalCta;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <SEOHead
        title={t('pilot_page.seo_title', 'Programa Piloto para Clínicas')}
        description={t('pilot_page.seo_desc', 'Quedan 2 de 3 cupos: implementación de automatización de WhatsApp sin costo de desarrollo a cambio de tu feedback.')}
        path="/piloto"
      />

      <style>{`
        .pilot-hero {
          padding: clamp(7rem, 12vh, 10rem) 2rem clamp(2.5rem, 5vh, 4rem);
          text-align: center;
        }
        .pilot-section {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 1.5rem clamp(3.5rem, 7vh, 6rem);
        }
        .pilot-section-title {
          font-family: var(--font-heading);
          font-size: clamp(1.4rem, 2.6vw, 1.9rem);
          font-weight: 700;
          color: var(--text-primary);
          text-align: center;
          margin: 0 0 2.2rem;
          letter-spacing: -0.01em;
        }
        .pilot-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 1.25rem;
        }
        .pilot-card {
          background: rgba(255,255,255,0.025);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          padding: clamp(1.6rem, 3vw, 2.1rem);
          display: flex;
          flex-direction: column;
          gap: 0.9rem;
          transition: border-color 0.3s, transform 0.3s;
        }
        .pilot-card:hover {
          border-color: var(--border-lighter);
          transform: translateY(-4px);
        }
        .pilot-icon-wrap {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .pilot-card-title {
          font-family: var(--font-heading);
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }
        .pilot-card-desc {
          font-size: 0.92rem;
          color: var(--text-secondary);
          line-height: 1.55;
          margin: 0;
        }
        .pilot-cta-card {
          position: relative;
          overflow: hidden;
          border-radius: 28px;
          padding: clamp(2.5rem, 5vw, 4rem) clamp(1.5rem, 4vw, 3rem);
          text-align: center;
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.15) 0%, rgba(139,92,246,0.11) 48%, rgba(8,8,8,0.6) 100%);
        }
        [data-theme='light'] .pilot-cta-card {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.16) 0%, rgba(139,92,246,0.1) 48%, rgba(255,255,255,0.92) 100%);
          box-shadow: 0 20px 50px -30px rgba(0,0,0,0.25);
        }
        .pilot-slots-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0.45rem 1rem;
          border-radius: 999px;
          font-family: var(--font-heading);
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 1.5rem;
          background: rgba(0,229,209,0.08);
          border: 1px solid rgba(0,229,209,0.25);
          color: var(--accent-teal);
        }
        .pilot-cta-btns {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 14px;
          margin: 2rem 0 1.4rem;
        }
        .pilot-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 1.05rem 2rem;
          border-radius: 980px;
          font-weight: 700;
          font-family: var(--font-heading);
          font-size: 1.02rem;
          text-decoration: none;
          cursor: pointer;
          border: none;
          background: var(--accent-teal);
          color: #00201d;
          box-shadow: 0 10px 30px -10px rgba(0,229,209,0.5);
          transition: transform 0.2s;
        }
        .pilot-btn-primary:hover { transform: translateY(-2px); }
        .pilot-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 1.05rem 1.8rem;
          border-radius: 16px;
          font-weight: 600;
          font-family: var(--font-heading);
          font-size: 0.98rem;
          color: var(--text-primary);
          cursor: pointer;
          text-decoration: none;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--border-lighter);
          transition: transform 0.2s;
        }
        [data-theme='light'] .pilot-btn-secondary { background: rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.12); }
        .pilot-btn-secondary:hover { transform: translateY(-2px); }

        /* Hero CTA — deja agendar de una vez a quien ya llega convencido
           desde la encuesta, sin obligarlo a bajar por todo el pitch. */
        .pilot-hero-cta {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.85rem;
          margin-top: 2rem;
        }
        .pilot-hero-wa-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0.5rem 0.4rem;
          margin: -0.5rem -0.4rem;
          color: var(--text-muted);
          font-size: 0.86rem;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s;
        }
        .pilot-hero-wa-link:hover { color: var(--text-secondary); }
        .pilot-hero-note {
          color: var(--text-muted);
          font-size: 0.78rem;
          letter-spacing: 0.02em;
        }

        /* Prueba social condensada — mismo caso real (Prodental 3D) que el
           Home, reutilizando las claves socialproof.* para no duplicar
           contenido ni mantenimiento en dos lugares. */
        .pilot-proof-card {
          position: relative;
          overflow: hidden;
          max-width: 900px;
          margin: 0 auto;
          border-radius: 24px;
          padding: clamp(1.9rem, 4vw, 2.6rem) clamp(1.5rem, 4vw, 2.8rem);
          background: var(--bg-elevated);
          border: 1px solid var(--border-light);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }
        [data-theme='light'] .pilot-proof-card {
          background: #fff;
          box-shadow: 0 25px 60px -30px rgba(0,0,0,0.15);
        }
        .pilot-proof-glow {
          position: absolute;
          top: -70px;
          right: -50px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: #00e5d1;
          opacity: 0.12;
          filter: blur(70px);
          pointer-events: none;
        }
        .pilot-proof-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--accent-teal);
          background: rgba(0,229,209,0.1);
          border: 1px solid rgba(0,229,209,0.3);
          padding: 0.35rem 0.85rem;
          border-radius: 999px;
          margin-bottom: 1.3rem;
        }
        .pilot-proof-quote {
          font-size: 1.05rem;
          font-weight: 300;
          line-height: 1.55;
          color: var(--text-primary);
          margin: 0 0 1.4rem;
          max-width: 640px;
        }
        .pilot-proof-body {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 2rem;
          align-items: center;
          position: relative;
          z-index: 1;
        }
        .pilot-proof-author {
          display: flex;
          align-items: center;
          gap: 0.7rem;
        }
        .pilot-proof-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-heading);
          font-weight: 800;
          font-size: 0.78rem;
          color: #fff;
          background: linear-gradient(135deg, #00e5d1 0%, #8b5cf6 100%);
          box-shadow: 0 0 0 3px rgba(0,229,209,0.1);
        }
        .pilot-proof-name { font-weight: 700; font-size: 0.88rem; color: var(--text-primary); }
        .pilot-proof-role { font-size: 0.78rem; color: var(--text-muted); }
        .pilot-proof-stats {
          display: flex;
          gap: 1.6rem;
          padding-left: 2rem;
          border-left: 1px solid var(--border-light);
        }
        .pilot-proof-stat-value {
          font-family: var(--font-heading);
          font-size: 1.3rem;
          font-weight: 800;
          color: var(--text-primary);
          letter-spacing: -0.02em;
          font-variant-numeric: tabular-nums;
        }
        .pilot-proof-stat-label {
          font-size: 0.72rem;
          color: var(--text-muted);
          max-width: 90px;
          line-height: 1.3;
        }
        @media (max-width: 700px) {
          .pilot-proof-body { grid-template-columns: 1fr; gap: 1.6rem; }
          .pilot-proof-stats { padding-left: 0; border-left: none; border-top: 1px solid var(--border-light); padding-top: 1.4rem; gap: 1.4rem; flex-wrap: wrap; }
        }

        /* Salida hacia el Home para quien todavía no está listo para agendar. */
        .pilot-explore-link {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          margin-top: 1.6rem;
          color: var(--text-muted);
          font-size: 0.84rem;
          font-weight: 600;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: color 0.2s, border-color 0.2s;
        }
        .pilot-explore-link:hover { color: var(--accent-teal); border-color: rgba(0,229,209,0.4); }

        /* Trust microcopy — junto al CTA, deja claro que la llamada es con
           una persona real (el fundador), no un bot ni un call center. */
        .pilot-trust-line {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: var(--text-secondary);
          font-size: 0.82rem;
          font-weight: 600;
        }

        /* Barra de agendar fija en móvil — solo existe por debajo de 700px;
           en desktop el botón "Agendar demo" del header ya cumple ese rol. */
        .pilot-sticky-cta {
          display: none;
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 40;
          padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom, 0px));
          background: var(--bg-elevated);
          border-top: 1px solid var(--border-light);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          transform: translateY(100%);
          transition: transform 0.3s cubic-bezier(0.16,1,0.3,1);
        }
        [data-theme='light'] .pilot-sticky-cta {
          background: rgba(255,255,255,0.94);
          box-shadow: 0 -10px 30px -20px rgba(0,0,0,0.25);
        }
        .pilot-sticky-cta.is-visible { transform: translateY(0); }
        @media (max-width: 700px) {
          .pilot-sticky-cta { display: block; }
        }
        .pilot-sticky-cta-btn {
          width: 100%;
          justify-content: center;
          padding: 0.9rem 1rem;
          font-size: 0.94rem;
        }
      `}</style>

      {/* Hero */}
      <section className="pilot-hero">
        <Reveal>
          <span
            style={{
              display: 'inline-block',
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--accent-teal)',
              marginBottom: '1.2rem',
              padding: '6px 14px',
              borderRadius: 999,
              border: '1px solid rgba(0,229,209,0.25)',
              background: 'rgba(0,229,209,0.06)',
            }}
          >
            {t('pilot_page.eyebrow', 'Programa piloto · Quedan 2 de 3 cupos')}
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
            fontWeight: 300,
            color: 'var(--text-primary)',
            margin: '0 0 1rem',
            lineHeight: 1.12,
            letterSpacing: '-0.03em',
          }}>
            {t('pilot_page.title_start', 'Diseñamos tu sistema a medida. ')}
            <span className="grad-text" style={{ fontWeight: 800 }}>
              {t('pilot_page.title_highlight', 'Tú no pagas el desarrollo.')}
            </span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p style={{
            color: 'var(--text-secondary)',
            maxWidth: 620,
            margin: '0 auto',
            fontSize: '1.08rem',
            lineHeight: 1.6,
          }}>
            {t('pilot_page.subtitle', 'Quedan 2 de 3 cupos para construir e implementar la automatización de WhatsApp que resuelve tus citas perdidas — sin costo de desarrollo, a cambio de tu feedback durante las primeras semanas.')}
          </p>
        </Reveal>

        {/* CTA arriba del fold: quien llega desde la encuesta ya calificado
            no debería tener que bajar por todo el pitch para agendar. */}
        <Reveal delay={0.28}>
          <div className="pilot-hero-cta">
            <Magnetic>
              <button
                data-cal-link={calLink()}
                data-cal-config='{"theme":"dark"}'
                className="pilot-btn-primary"
              >
                <CalendarClock size={18} />
                {t('pilot_page.cta_btn_cal', 'Agendar mi diagnóstico gratuito')}
                <ArrowRight size={18} />
              </button>
            </Magnetic>
            <a
              href={waLink(t('pilot_page.wa_msg', 'Hola, quiero postular mi clínica al programa piloto de RurayHUB'))}
              target="_blank"
              rel="noopener noreferrer"
              className="pilot-hero-wa-link"
            >
              {t('pilot_page.hero_wa_link', 'o prefiero escribir por WhatsApp')}
            </a>
            <span className="pilot-trust-line">
              <PhoneCall size={14} color="var(--accent-teal)" />
              {t('pilot_page.trust_line', 'Hablas directo con el fundador — no con un bot ni un call center')}
            </span>
            <span className="pilot-hero-note">
              {t('pilot_page.hero_note', 'Quedan 2 de 3 cupos disponibles · Sin costo · 15-20 minutos')}
            </span>
          </div>
        </Reveal>
        <div ref={heroSentinelRef} aria-hidden style={{ height: 1 }} />
      </section>

      {/* Prueba social — antes de listar los dolores, mostramos que esto ya
          funciona con un cliente real. Reutiliza las claves socialproof.*
          (mismo caso, mismas cifras, misma cita que en el Home). */}
      <div className="pilot-section" style={{ paddingTop: 0 }}>
        <Reveal>
          <div className="pilot-proof-card">
            <div aria-hidden className="pilot-proof-glow" />
            <span className="pilot-proof-badge">
              <Sparkles size={12} strokeWidth={2.5} />
              {t('socialproof.case_badge', 'Tecnología dental · Caso real')}
            </span>
            <p className="pilot-proof-quote">
              <Quote size={18} color="var(--accent-teal)" style={{ opacity: 0.5, marginRight: 6, verticalAlign: '-2px' }} />
              <span style={{ color: 'var(--accent-teal)', fontWeight: 700 }}>{t('socialproof.case_quote_highlight', 'Ahora el agente responde por nosotros mientras dormimos')}</span>
              {t('socialproof.case_quote_post', ', y la inversión ya se recuperó sola.')}
            </p>
            <div className="pilot-proof-body">
              <div className="pilot-proof-author">
                <span className="pilot-proof-avatar">GM</span>
                <div>
                  <div className="pilot-proof-name">{t('socialproof.case_quote_name', 'Gustavo Mendez')}</div>
                  <div className="pilot-proof-role">{t('socialproof.case_quote_role', 'Gerente General de Prodental 3D')}</div>
                </div>
              </div>
              <div className="pilot-proof-stats">
                <div>
                  <div className="pilot-proof-stat-value">{t('socialproof.case_stat1_value', '1.032')}</div>
                  <div className="pilot-proof-stat-label">{t('socialproof.case_stat1_label', 'conversaciones con IA este mes')}</div>
                </div>
                <div>
                  <div className="pilot-proof-stat-value">{t('socialproof.case_stat2_value', '88%')}</div>
                  <div className="pilot-proof-stat-label">{t('socialproof.case_stat2_label', 'de automatización')}</div>
                </div>
                <div>
                  <div className="pilot-proof-stat-value">{t('socialproof.case_stat3_value', '137,6h')}</div>
                  <div className="pilot-proof-stat-label">{t('socialproof.case_stat3_label', 'de trabajo manual ahorradas')}</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Pain points */}
      <div className="pilot-section">
        <Reveal>
          <h2 className="pilot-section-title">{t('pilot_page.pain_title', '¿Te suena familiar?')}</h2>
        </Reveal>
        <div className="pilot-grid">
          {PAIN_POINTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="pilot-card">
                  <div className="pilot-icon-wrap" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}>
                    <Icon size={22} color="#ef4444" />
                  </div>
                  <h3 className="pilot-card-title">{t(`pilot_page.pain_${i + 1}_title`, p.title)}</h3>
                  <p className="pilot-card-desc">{t(`pilot_page.pain_${i + 1}_desc`, p.desc)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* What's included */}
      <div className="pilot-section">
        <Reveal>
          <h2 className="pilot-section-title">{t('pilot_page.includes_title', 'Qué incluye el programa')}</h2>
        </Reveal>
        <div className="pilot-grid">
          {INCLUDES.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="pilot-card">
                  <div className="pilot-icon-wrap" style={{ background: 'rgba(0,229,209,0.1)', border: '1px solid rgba(0,229,209,0.25)' }}>
                    <Icon size={22} color="var(--accent-teal)" />
                  </div>
                  <h3 className="pilot-card-title">{t(`pilot_page.include_${i + 1}_title`, p.title)}</h3>
                  <p className="pilot-card-desc">{t(`pilot_page.include_${i + 1}_desc`, p.desc)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="pilot-section" ref={finalCtaRef}>
        <Reveal>
          <div className="pilot-cta-card">
            <span className="pilot-slots-badge">
              {t('pilot_page.slots_badge', 'Quedan 2 de 3 cupos disponibles para garantizar una implementación personalizada')}
            </span>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'clamp(1.6rem, 3.4vw, 2.4rem)',
              fontWeight: 300,
              color: 'var(--text-primary)',
              margin: '0 0 0.9rem',
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
            }}>
              {t('pilot_page.cta_title', 'Agenda tu diagnóstico de 15-20 minutos')}
            </h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: 480, margin: '0 auto', fontSize: '1rem', lineHeight: 1.6 }}>
              {t('pilot_page.cta_subtitle', 'Revisamos el flujo actual de tu clínica y vemos si aplica al programa. Sin compromiso.')}
            </p>

            <div className="pilot-cta-btns">
              <Magnetic>
                <button
                  data-cal-link={calLink()}
                  data-cal-config='{"theme":"dark"}'
                  className="pilot-btn-primary"
                >
                  <CalendarClock size={18} />
                  {t('pilot_page.cta_btn_cal', 'Agendar mi diagnóstico gratuito')}
                  <ArrowRight size={18} />
                </button>
              </Magnetic>
              <Magnetic>
                <a
                  href={waLink(t('pilot_page.wa_msg', 'Hola, quiero postular mi clínica al programa piloto de RurayHUB'))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pilot-btn-secondary"
                >
                  {t('pilot_page.cta_btn_wa', 'Prefiero escribir por WhatsApp')}
                </a>
              </Magnetic>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
              <span className="pilot-trust-line">
                <PhoneCall size={14} color="var(--accent-teal)" />
                {t('pilot_page.trust_line', 'Hablas directo con el fundador — no con un bot ni un call center')}
              </span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.84rem' }}>
                <ShieldCheck size={15} color="var(--text-primary)" />
                {t('pilot_page.guarantee', 'Sin costo · Sin compromiso · 15-20 minutos')}
              </div>
            </div>

            <div>
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="pilot-explore-link"
              >
                {t('pilot_page.explore_home_link', '¿Prefieres conocer todo el producto primero? Ver cómo funciona RurayHUB')}
                <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      {createPortal(
        <div data-fixed-bottom-bar className={`pilot-sticky-cta${showStickyBar ? ' is-visible' : ''}`}>
          <button
            data-cal-link={calLink()}
            data-cal-config='{"theme":"dark"}'
            className="pilot-btn-primary pilot-sticky-cta-btn"
          >
            <CalendarClock size={17} />
            {t('pilot_page.sticky_cta_btn', 'Agendar diagnóstico gratis')}
          </button>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default PilotProgramPage;
